// Import the OpenAI SDK
import OpenAI from 'openai';

// Initialize OpenAI with environment variable API key 
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Allow browser usage
});

export const generateSalesDescription = async (vehicleDetails) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Write a compelling and professional vehicle sales description for a ${vehicleDetails}. Include key selling points, highlight features that customers value, and use persuasive language that would appeal to potential buyers. Keep it under 150 words.`,
      max_tokens: 250,
      temperature: 0.7,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating sales description:', error);
    return 'Could not generate description. Please try again later.';
  }
};

export const generateFollowupEmail = async (customerName, vehicleInterest, previousInteraction) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Write a personalized follow-up email to ${customerName} who is interested in a ${vehicleInterest}. Their last interaction with us was: "${previousInteraction}". Be professional, friendly, and persuasive. Include a call to action.`,
      max_tokens: 300,
      temperature: 0.7,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating follow-up email:', error);
    return 'Could not generate email. Please try again later.';
  }
};

export const analyzeSentiment = async (customerFeedback) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Analyze the sentiment of this customer feedback and categorize it as Positive, Neutral, or Negative. Also identify key themes or issues mentioned: "${customerFeedback}"`,
      max_tokens: 150,
      temperature: 0.3,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error analyzing sentiment:', error);
    return 'Could not analyze feedback. Please try again later.';
  }
};

export const generateLeadQualification = async (leadInfo) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Based on this lead information, provide a qualification score from 1-10 and explain why: Name: ${leadInfo.firstName} ${leadInfo.lastName} Vehicle Interest: ${leadInfo.interestedVehicle || 'Not specified'} Budget: ${leadInfo.budget ? '$' + leadInfo.budget : 'Not specified'} Source: ${leadInfo.source || 'Not specified'} Notes: ${leadInfo.notes || 'None'}`,
      max_tokens: 200,
      temperature: 0.5,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating lead qualification:', error);
    return 'Could not qualify lead. Please try again later.';
  }
};

export const suggestSalesStrategy = async (customerProfile) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Suggest a personalized sales strategy for this customer: Name: ${customerProfile.firstName} ${customerProfile.lastName} Status: ${customerProfile.status} Vehicle Interest: ${customerProfile.interestedVehicle || 'Not specified'} Budget: ${customerProfile.budget ? '$' + customerProfile.budget : 'Not specified'} Previous Interactions: ${customerProfile.notes || 'None'} Provide specific talking points, features to highlight, and negotiation strategies.`,
      max_tokens: 300,
      temperature: 0.7,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating sales strategy:', error);
    return 'Could not generate sales strategy. Please try again later.';
  }
};

// New AI features

export const generateCompetitiveComparison = async (vehicleDetails, competitors) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Create a competitive analysis comparing the ${vehicleDetails} with these competitors: ${competitors}. Highlight advantages of our vehicle in terms of features, performance, value, and reliability. Format the response as bullet points for easy sharing with customers.`,
      max_tokens: 400,
      temperature: 0.7,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating competitive comparison:', error);
    return 'Could not generate comparison. Please try again later.';
  }
};

export const generateFinancingOptions = async (customerBudget, vehiclePrice, creditScore) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Generate personalized financing options for a customer with these details: Budget: $${customerBudget}, Vehicle Price: $${vehiclePrice}, Credit Score Range: ${creditScore}. Include monthly payment estimates for 36, 48, and 60-month terms with appropriate interest rates based on credit score. Add recommendations for down payment amounts and explain benefits of each option.`,
      max_tokens: 350,
      temperature: 0.5,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating financing options:', error);
    return 'Could not generate financing options. Please try again later.';
  }
};

export const generatePersonalizedOffers = async (customerProfile, vehicleHistory) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Create personalized special offers for this customer: ${JSON.stringify(customerProfile)}. Their vehicle history: ${vehicleHistory}. Generate 3 tailored offers that might appeal to them based on their profile, including service packages, accessories, or financing incentives. Make each offer specific and compelling.`,
      max_tokens: 350,
      temperature: 0.7,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating personalized offers:', error);
    return 'Could not generate offers. Please try again later.';
  }
};

export const generateCustomerInsights = async (customerInteractions) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Based on these customer interactions: ${JSON.stringify(customerInteractions)}, provide insights about this customer's preferences, communication style, decision-making factors, and potential objections. Include recommendations for how to best communicate with and serve this customer.`,
      max_tokens: 350,
      temperature: 0.6,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating customer insights:', error);
    return 'Could not generate customer insights. Please try again later.';
  }
};

export const generateMarketTrends = async (vehicleCategory, region) => {
  try {
    const response = await openai.completions.create({
      model: "gpt-3.5-turbo-instruct",
      prompt: `Provide current market trends analysis for ${vehicleCategory} vehicles in the ${region} region. Include information about demand patterns, popular features, pricing trends, competitor activity, and consumer preferences. Conclude with actionable recommendations for sales representatives.`,
      max_tokens: 400,
      temperature: 0.6,
    });
    return response.choices[0].text.trim();
  } catch (error) {
    console.error('Error generating market trends:', error);
    return 'Could not generate market trends. Please try again later.';
  }
};